import React, { useEffect, useState } from "react";
import { Badge, Calendar, Button, Typography, Flex } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { formatDate } from "../../Utils/generic-functions";
import "./index.scss";
import InterviewService from "../../Services/interview.service";
import AgentService from "../../Services/agent/agent.service";
import messages from "../../Services/agent/messages";
import { openNotification } from "../Notification/Index";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import utc from "dayjs/plugin/utc";
import { JobApplication } from "../../Entities/job-application.entity";
import SlotPickerModal from "./SlotPickerModal";
import { useLoader } from "../../Context/LoaderContext/LoaderContext";

dayjs.extend(utc);

interface CalendarSlotProps {
  onSlotBooked: () => void;
  jobPostId: string;
  appliedJob: JobApplication;
}

const CalendarSlot: React.FC<CalendarSlotProps> = ({
  onSlotBooked,
  jobPostId,
  appliedJob,
}) => {
  const { setLoading, setLoadingMessage } = useLoader();
  const interviewService = new InterviewService();
  const [slots, setSlots] = useState<any>([]);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [showSlotDetails, setShowSlotDetails] = useState(false);

  useEffect(() => {
    const fetchAvailableSlotData = async (id: string) => {
      setLoading(true);
      setLoadingMessage("Fetching available slots...");
      const data = await interviewService.getAvailableInterviewSlots(id);
      setSlots(data);
      setLoading(false);
      setLoadingMessage(null);
    };

    if (jobPostId) {
      fetchAvailableSlotData(jobPostId);
    }
  }, [jobPostId]);

  const isSlotsAvailable = (value: Dayjs) => {
    const filteredSlots = slots.filter((slot: any) => {
      const start = new Date(slot.start);
      const end = new Date(slot.end);

      if (
        start.getDate() === value.date() &&
        start.getMonth() === value.month() &&
        start.getFullYear() === value.year()
      ) {
        return true;
      }

      if (
        start.getDate() !== end.getDate() &&
        end.getDate() === value.date() &&
        start.getMonth() === end.getMonth() &&
        start.getFullYear() === value.year()
      ) {
        return false;
      }

      return false;
    });
    return filteredSlots.length > 0;
  };

  const dateCellRender = (value: Dayjs) => {
    const slotsAvailable = isSlotsAvailable(value);

    return (
      <div
        className={`events ${!slotsAvailable ? "disabled-cell" : ""}`}
        onClick={() => {
          if (slotsAvailable) {
            setSelectedDate(value);
            setSelectedSlot(null);
          }
        }}
      >
        {slotsAvailable && (
          <Badge
            status="success"
            text={<span className="hidden-on-small">Slots Available</span>}
          />
        )}
      </div>
    );
  };

  const handleMonthChange = (next: boolean) => {
    const newMonth = currentMonth.add(next ? 1 : -1, "month");
    setCurrentMonth(newMonth);
    const currentDay = selectedDate.date();
    const newSelectedDate = newMonth.date(currentDay);
    setSelectedDate(newSelectedDate);
  };

  const handleBookSlot = async () => {
    setLoading(true);
    if (selectedSlot) {
      const startUTC = dayjs(selectedSlot.start).utc().toISOString();
      const endUTC = dayjs(selectedSlot.end).utc().toISOString();
      const data = await interviewService.bookInterviewSlot(
        appliedJob._id,
        startUTC,
        endUTC
      );
      const intervalId = setInterval(async () => {
        const isMeetingLinkAvailable =
          await interviewService.checkForMeetingLink(appliedJob._id);
        if (isMeetingLinkAvailable) {
          clearInterval(intervalId);
          openNotification("success", "Slot Booked!", "");
          AgentService.getInstance().setCurrentMessage(
            messages.INTERVIEW_SCHEDULED_UPCOMING,
            { interview_slot: formatDate(selectedSlot.start) },
            "View Schedule"
          );
          onSlotBooked();
          setSelectedSlot(null);
          setSelectedDate(dayjs());
          setLoading(false);
        }
      }, 2000);
      setTimeout(() => clearInterval(intervalId), 8000);
    } else {
      setLoading(false);
    }
  };

  const renderMonthInfo = () => {
    return (
      <Flex
        align="center"
        justify="space-between"
        style={{ width: "100%", marginBottom: 16 }}
      >
        <Button
          disabled={currentMonth.isSame(dayjs(), "month")}
          icon={<LeftOutlined />}
          onClick={() => handleMonthChange(false)}
        />

        <Typography.Title level={4} style={{ margin: 0 }}>
          {currentMonth.format("MMMM YYYY")}
        </Typography.Title>

        <Button
          icon={<RightOutlined />}
          onClick={() => handleMonthChange(true)}
        />
      </Flex>
    );
  };

  const handleDateClick = (date: Dayjs) => {
    setSelectedDate(date);
    const slotCheckForDate = isSlotsAvailable(date);
    if (slotCheckForDate) {
      setShowSlotDetails(true);
    } else {
      setShowSlotDetails(false);
      openNotification("info", "", "No slots available for selected date");
    }
  };

  const handleSlotPickerModalClose = () => {
    setShowSlotDetails(false);
    setSelectedSlot(null);
  };

  const handleSlotChange = (slot: any) => {
    if (selectedSlot === slot) {
      setSelectedSlot(null);
    } else {
      setSelectedSlot(slot);
    }
  };

  return (
    <div className="calendar-container">
      {renderMonthInfo()}
      <Calendar
        value={selectedDate}
        cellRender={dateCellRender}
        mode="month"
        fullscreen={false}
        headerRender={() => null}
        onSelect={(date) => handleDateClick(date)}
        onPanelChange={(date) => setCurrentMonth(date)}
      />
      {selectedDate && showSlotDetails && (
        <SlotPickerModal
          handleSlotPickerModalClose={handleSlotPickerModalClose}
          selectedDate={selectedDate}
          handleBookSlot={handleBookSlot}
          selectedSlot={selectedSlot}
          slots={slots}
          handleSlotChange={handleSlotChange}
        />
      )}
    </div>
  );
};

export default CalendarSlot;
